import React from "react";
import { computed, observable } from "mobx";
import { observer } from "mobx-react";
import {
  withRouter,
  RouteComponentProps,
  generatePath,
} from "react-router-dom";
import { FormSpy } from "react-final-form";

import KeyboardBackspaceIcon from "@material-ui/icons/KeyboardBackspace";

import { Button, FormLabel } from "@material-ui/core";

import Page from "components/Layout/Page";
import LoadingButton from "components/LoadingButton";
import FormControl, { TextFieldControl } from "components/FormControl";
import DropdownFieldControl from "components/FormControl/DropdownFieldControl";
import SubmitButtonControl, {
  CancelButtonControl,
} from "components/FormControl/SubmitButtonControl";
import {
  withGlobalSnackbar,
  SnackbarContextProps,
} from "services/snackbar/snackbarContext";
import withGlobalPopup, {
  GlobalPopupProps,
} from "components/Popup/withGlobalPopup";
import Popup from "components/Popup";
import ConfirmationDialog from "components/ConfirmationDialog";
import PublishStatus from "components/PublishStatus";
import SimpleField from "components/SimpleField";

import { SNACKBAR_TYPES } from "components/Snackbar";

import { imageTypesValidator, lengthValidator } from "utils/validators";

import {
  PUBLISH_STATUSES,
  RELATED_DATA,
  RELATED_DATA_TEXT,
  RESPONSE_TYPES,
  RESPONSE_TYPES_TEXT,
} from "constants/models";

import ROUTES from "navigation/routes";

import {
  BackButtonWrapper,
  Container,
  ControlsContainer,
  ControlContainer,
  Column,
  GroupRow,
  Row,
  Divider,
  PublishStatusWrapper,
  GroupRowLabelContainer,
  OptionsCardWrapper,
} from "./styles";
import QuestionStore from "stores/QuestionStore";
import { OptionCards } from "./OptionControl/styles";
import OptionCard from "./OptionControl/OptionCard";
import OptionControl from "./OptionControl";
import SectionDictionary from "stores/dictionary/SectionDictionary";
import RelatedDataDictionary from "stores/dictionary/RelatedDataDictionary";
import ResponseTypeDictionary from "stores/dictionary/ResponseTypeDictionary";

type LocationState = {
  editMode: boolean;
  readOnly?: boolean;
};

type Props = GlobalPopupProps &
  RouteComponentProps<{ questionId: string }, {}, LocationState> &
  SnackbarContextProps & {};

@observer
class QuestionDetails extends React.Component<Props> {
  @observable isPopupDirty: boolean = false;
  @observable closeConfirmationIsOpened: boolean = false;
  @observable updateConfirmationIsOpened: boolean = false;
  @observable onAccept: () => void = () => {};

  get locationState() {
    return this.props.location.state;
  }

  get isEditMode() {
    return this.locationState?.editMode;
  }

  get isReadOnlyMode() {
    return this.locationState?.readOnly;
  }

  get isNewQuestion() {
    const { questionId } = this.props.match.params;

    return questionId === undefined;
  }

  get questionId() {
    const { questionId } = this.props.match.params;

    const parsedQuestionId = Number.parseInt(questionId);
    return typeof parsedQuestionId === "number" ? parsedQuestionId : undefined;
  }

  get backClickText() {
    return " Back to Questions";
  }

  get backClickPath() {
    return ROUTES.CONTENT_QUESTIONNAIRE_QUESTION;
  }

  @computed get displayValues(): any {
    const formData = QuestionStore.details?.formData;

    const responseType = this.responseTypeOptions.find(
      obj => obj.value === formData?.responseTypeId,
    );

    const relatedData = this.relatedDataOptions.find(
      obj => obj.value === formData?.relatedDataId,
    );

    return {
      ...formData,
      responseTypeLabel: responseType?.label,
      relatedDataLabel: relatedData?.label,
    };
  }

  @computed get initialValues() {
    if (this.isNewQuestion) {
      return {};
    } else {
      return {
        ...QuestionStore.details?.formData,
      };
    }
  }

  @computed get questionPublishStatus() {
    return QuestionStore.details?.publishStatus;
  }

  @computed get durationText() {
    return this.displayValues?.isVideoReady
      ? this.displayValues?.durationText
      : "Video processing is still in progress. Video duration will be automatically updated later.";
  }

  async componentDidMount() {
    const { history } = this.props;

    Promise.all([
      SectionDictionary.fetch(),
      RelatedDataDictionary.fetch(),
      ResponseTypeDictionary.fetch(),
    ]);

    if (this.isNewQuestion) {
      history.replace({ state: { editMode: true } });
    } else {
      await QuestionStore.fetchQuestionDetails(this.questionId);
    }
  }

  componentWillUnmount() {
    QuestionStore.resetDetails();
  }

  onResponseTypeOption = () => {
    return [
      {
        value: RESPONSE_TYPES.TEXT,
        label: RESPONSE_TYPES_TEXT.TEXT,
      },
      {
        value: RESPONSE_TYPES.EMAIL,
        label: RESPONSE_TYPES_TEXT.EMAIL,
      },
      {
        value: RESPONSE_TYPES.UNIQUE,
        label: RESPONSE_TYPES_TEXT.UNIQUE,
      },
      {
        value: RESPONSE_TYPES.MULTIPLE,
        label: RESPONSE_TYPES_TEXT.MULTIPLE,
      },
      {
        value: RESPONSE_TYPES.TEXT_AREA,
        label: RESPONSE_TYPES_TEXT.TEXT_AREA,
      },
      {
        value: RESPONSE_TYPES.NUMBER,
        label: RESPONSE_TYPES_TEXT.NUMBER,
      },
      {
        value: RESPONSE_TYPES.DECIMAL,
        label: RESPONSE_TYPES_TEXT.DECIMAL,
      },
      {
        value: RESPONSE_TYPES.CALENDAR,
        label: RESPONSE_TYPES_TEXT.CALENDAR,
      },
    ];
  };

  onRequiredOption = () => {
    return [
      {
        value: "true",
        label: "Yes",
      },
      {
        value: "false",
        label: "No",
      },
    ];
  };

  onRelatedDataOption = () => {
    return [
      {
        value: RELATED_DATA.ARM,
        label: RELATED_DATA_TEXT.ARM,
      },
      {
        value: RELATED_DATA.BUST,
        label: RELATED_DATA_TEXT.BUST,
      },
      {
        value: RELATED_DATA.BUTT,
        label: RELATED_DATA_TEXT.BUTT,
      },
      {
        value: RELATED_DATA.DATE_OF_BIRTH,
        label: RELATED_DATA_TEXT.DATE_OF_BIRTH,
      },
      {
        value: RELATED_DATA.EMAIL,
        label: RELATED_DATA_TEXT.EMAIL,
      },
      {
        value: RELATED_DATA.GENDER,
        label: RELATED_DATA_TEXT.GENDER,
      },
      {
        value: RELATED_DATA.HIPS,
        label: RELATED_DATA_TEXT.HIPS,
      },
      {
        value: RELATED_DATA.HEIGHT,
        label: RELATED_DATA_TEXT.HEIGHT,
      },
      {
        value: RELATED_DATA.LOCATION,
        label: RELATED_DATA_TEXT.LOCATION,
      },
      {
        value: RELATED_DATA.NAME,
        label: RELATED_DATA_TEXT.NAME,
      },
      {
        value: RELATED_DATA.PHONE,
        label: RELATED_DATA_TEXT.PHONE,
      },
      {
        value: RELATED_DATA.THIGH,
        label: RELATED_DATA_TEXT.THIGH,
      },
      {
        value: RELATED_DATA.WAIST,
        label: RELATED_DATA_TEXT.WAIST,
      },
      {
        value: RELATED_DATA.WEIGHT,
        label: RELATED_DATA_TEXT.WEIGHT,
      },
    ];
  };

  @computed get sectionOptions() {
    return [...SectionDictionary.options];
  }

  @computed get responseTypeOptions() {
    return [...ResponseTypeDictionary.options];
  }

  @computed get relatedDataOptions() {
    return [...RelatedDataDictionary.options];
  }

  formFileValidator = (acceptedTypes: string) => (value?: File) =>
    imageTypesValidator(value?.type ?? "", acceptedTypes);

  closeConfirmationPopup = () => {
    this.closeConfirmationIsOpened = false;
  };

  onCloseForm = (onAccept: () => void) => {
    if (this.isPopupDirty) {
      this.closeConfirmationIsOpened = true;
      this.onAccept = onAccept;
    } else {
      onAccept();
    }
  };

  onBackClick = () => {
    const onAccept = () => this.props.history.push(this.backClickPath);

    if (this.isEditMode) {
      this.onCloseForm(onAccept);
    } else {
      onAccept();
    }
  };

  onCancelClick = (resetForm: () => void) => {
    if (this.isNewQuestion) {
      this.onBackClick();
    } else {
      const onAccept = () => {
        this.exitEditMode();
        this.closeConfirmationPopup();
        this.resetIsPopupDirty();
        resetForm();
      };
      this.onCloseForm(onAccept);
    }
  };

  onEditClick = () => {
    const { history } = this.props;

    const path = generatePath(ROUTES.CONTENT_QUESTIONNAIRE_QUESTION_DETAILS, {
      questionId: this.questionId,
    });
    history.replace({ pathname: path, state: { editMode: true } });
  };

  exitEditMode = () => {
    const { history } = this.props;

    const path = generatePath(ROUTES.CONTENT_QUESTIONNAIRE_QUESTION_DETAILS, {
      questionId: this.questionId,
    });
    history.replace({ pathname: path, state: { editMode: false } });
  };

  onChangeIsPopupDirty = ({ dirty }: { dirty: boolean }) => {
    this.isPopupDirty = dirty;
  };

  resetIsPopupDirty = () => {
    this.isPopupDirty = false;
  };

  openUpdateConfirmation = () => {
    this.updateConfirmationIsOpened = true;
  };

  closeUpdateConfirmation = () => {
    this.updateConfirmationIsOpened = false;
  };

  confirmUpdate = (values: any) => () => {
    this.closeUpdateConfirmation();
    this.submit(values);
  };

  onSubmit = (values: any) => {
    const shouldShowUpdateConfirmation =
      this.questionPublishStatus &&
      [
        PUBLISH_STATUSES.PUBLISHED,
        PUBLISH_STATUSES.SCHEDULE_FOR_REMOVE,
      ].includes(this.questionPublishStatus);

    shouldShowUpdateConfirmation
      ? this.openUpdateConfirmation()
      : this.submit(values);
  };

  submit = async (values: any) => {
    const { setMessage, history } = this.props;
    const {
      id,
      title,
      code,
      section,
      complement,
      responseTypeId,
      isRequired,
      relatedDataId,
    } = values;

    const data = {
      title: title?.trim(),
      code: code?.trim(),
      complement: complement?.trim(),
      responseType: `${responseTypeId}`,
      isRequired: isRequired === "true",
      sectionID: section?.id,
      relatedData: relatedDataId ?? null,
    };

    try {
      if (this.isNewQuestion) {
        const id = await QuestionStore.createQuestion(data);
        if (id) {
          const path = generatePath(
            ROUTES.CONTENT_QUESTIONNAIRE_QUESTION_DETAILS,
            {
              questionId: id,
            },
          );
          history.push({ pathname: path, state: { editMode: false } });
          QuestionStore.fetchQuestionDetails(id);
        }
      } else {
        const payload = {
          ...data,
          id,
        };
        await QuestionStore.editQuestionInfo(payload);

        this.exitEditMode();
        QuestionStore.fetchQuestionDetails(id);
      }
      this.resetIsPopupDirty();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  shouldShowConfirmation = () => {
    return this.isPopupDirty;
  };

  onChangeResponseTypeCallback = (newValue: string, previousValue: string) => {
    console.log(newValue);
    console.log(previousValue);
  };

  render() {
    return (
      <Page
        title="Question Details"
        leftControls={
          this.questionPublishStatus && (
            <PublishStatusWrapper>
              <PublishStatus status={this.questionPublishStatus} />
            </PublishStatusWrapper>
          )
        }
        topControls={
          <BackButtonWrapper>
            <Button
              color="secondary"
              variant="text"
              startIcon={<KeyboardBackspaceIcon />}
              onClick={this.onBackClick}
            >
              {this.backClickText}
            </Button>
          </BackButtonWrapper>
        }
        isLoading={QuestionStore.isQuestionLoading}
      >
        {this.closeConfirmationIsOpened && (
          <Popup onClose={this.closeConfirmationPopup} withoutExitButton>
            <ConfirmationDialog
              title="Discard Changes"
              text="Are you sure you would like to discard changes? All information will be lost."
              declineText="Cancel"
              acceptText="Discard"
              onDecline={this.closeConfirmationPopup}
              onAccept={this.onAccept}
            />
          </Popup>
        )}
        <FormControl
          onSubmit={this.onSubmit}
          initialValues={this.initialValues}
          keepDirtyOnReinitialize={this.isNewQuestion}
        >
          {this.updateConfirmationIsOpened && (
            <FormSpy subscription={{ values: true }}>
              {({ values }) => (
                <Popup onClose={this.closeUpdateConfirmation} withoutExitButton>
                  <ConfirmationDialog
                    title="Save Changes to Production"
                    text="All changes made will be published to production. Are you sure you want to proceed?"
                    acceptText="Proceed"
                    declineText="Cancel"
                    onAccept={this.confirmUpdate(values)}
                    onDecline={this.closeUpdateConfirmation}
                  />
                </Popup>
              )}
            </FormSpy>
          )}
          <FormSpy
            subscription={{ dirty: true }}
            onChange={this.onChangeIsPopupDirty}
          />
          <Container>
            <ControlsContainer>
              {this.isEditMode ? (
                <>
                  <ControlContainer>
                    <CancelButtonControl
                      render={this.renderCancelButton}
                      onClick={this.onCancelClick}
                    />
                  </ControlContainer>
                  <ControlContainer>
                    <SubmitButtonControl render={this.renderButton} />
                  </ControlContainer>
                </>
              ) : (
                !this.isReadOnlyMode && (
                  <ControlContainer>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={this.onEditClick}
                      fullWidth
                    >
                      Edit
                    </Button>
                  </ControlContainer>
                )
              )}
            </ControlsContainer>

            <GroupRow>
              <Column>
                <Row>
                  <Column>
                    {this.isEditMode ? (
                      <TextFieldControl
                        variant="outlined"
                        margin="none"
                        name="title"
                        label="Title"
                        validate={lengthValidator(128)}
                        fullWidth
                        required
                      />
                    ) : (
                      <SimpleField
                        label="Title"
                        value={this.displayValues?.title}
                      />
                    )}
                  </Column>
                  <Column>
                    {this.isEditMode ? (
                      <TextFieldControl
                        variant="outlined"
                        margin="none"
                        name="code"
                        label="Code"
                        validate={lengthValidator(64)}
                        fullWidth
                        required
                      />
                    ) : (
                      <SimpleField
                        label="code"
                        value={this.displayValues?.code}
                      />
                    )}
                  </Column>
                </Row>
                <GroupRow>
                  <Column>
                    <Row>
                      {this.isEditMode ? (
                        <TextFieldControl
                          name="complement"
                          variant="outlined"
                          margin="none"
                          label="Complement"
                          validate={lengthValidator(1024)}
                          rows={5}
                          rowsMax={10}
                          disabled={!this.isEditMode}
                          fullWidth
                          multiline
                        />
                      ) : (
                        <SimpleField
                          label="Complement"
                          value={this.displayValues?.complement}
                        />
                      )}
                    </Row>
                  </Column>
                  <Column>
                    {this.isEditMode ? (
                      <DropdownFieldControl
                        name="section"
                        options={this.sectionOptions}
                        label="Section"
                        multi={false}
                        isClearable
                        required={false}
                      />
                    ) : (
                      <SimpleField
                        label="Section"
                        value={this.displayValues?.section?.title}
                      />
                    )}
                  </Column>
                </GroupRow>
                <Row>
                  <Column>
                    {this.isEditMode ? (
                      <DropdownFieldControl
                        name="responseTypeId"
                        options={this.responseTypeOptions}
                        label="Response Type"
                        multi={false}
                        isClearable
                        required
                      />
                    ) : (
                      <SimpleField
                        label="Response Type"
                        value={this.displayValues?.responseTypeLabel}
                      />
                    )}
                  </Column>
                  <Column>
                    {this.isEditMode ? (
                      <DropdownFieldControl
                        name="relatedDataId"
                        options={this.relatedDataOptions}
                        label="Related Data"
                        multi={false}
                        isClearable
                        required={false}
                      />
                    ) : (
                      <SimpleField
                        label="Related Data"
                        value={this.displayValues?.relatedDataLabel}
                      />
                    )}
                  </Column>
                </Row>
                <Row>
                  <Column>
                    {this.isEditMode ? (
                      <DropdownFieldControl
                        name="isRequired"
                        options={this.onRequiredOption()}
                        label="Required"
                        multi={false}
                        onChangeCallback={this.onChangeResponseTypeCallback}
                        isClearable
                        required
                      />
                    ) : (
                      <SimpleField
                        label="Required"
                        value={
                          this.displayValues?.isRequired === "true"
                            ? "Yes"
                            : "No"
                        }
                      />
                    )}
                  </Column>
                  <Column></Column>
                </Row>
                {(this.displayValues?.responseTypeId === "Unique" ||
                  this.displayValues?.responseTypeId === "Multiple" ||
                  this.displayValues?.responseTypeId === "Unique") && (
                  <GroupRow>
                    <Column>
                      {this.isEditMode ? (
                        <Row>
                          <OptionControl
                            questionID={this.displayValues?.id}
                            name="options"
                          />
                        </Row>
                      ) : (
                        <>
                          <GroupRowLabelContainer>
                            <FormLabel>Options</FormLabel>
                          </GroupRowLabelContainer>
                          <OptionsCardWrapper>
                            <OptionCards>
                              {this.displayValues?.options?.map((item: any) => (
                                <OptionCard key={item.id} title={item.title} />
                              ))}
                            </OptionCards>
                          </OptionsCardWrapper>
                        </>
                      )}
                    </Column>
                  </GroupRow>
                )}
              </Column>
            </GroupRow>
            <Divider />
          </Container>
        </FormControl>
      </Page>
    );
  }

  renderButton = ({ onClick }: { onClick: () => void }) => (
    <LoadingButton
      onClick={onClick}
      variant="contained"
      color="primary"
      isLoading={QuestionStore.isQuestionManaging}
      fullWidth
    >
      {this.isNewQuestion ? "Add" : "Save"}
    </LoadingButton>
  );

  renderCancelButton = ({ onClick }: { onClick: () => void }) => (
    <Button variant="outlined" color="primary" onClick={onClick} fullWidth>
      Cancel
    </Button>
  );
}

export default withGlobalPopup(withGlobalSnackbar(withRouter(QuestionDetails)));
